import React, { useState, useMemo } from 'react';
import { exportTableAsExcel, exportTableAsPdf } from '../../utils/exportTable';
import './DataTable.css';

const DataTable = ({ 
  data = [], 
  columns = [], 
  loading = false, 
  loadingMessage = "Cargando datos...",
  emptyMessage = "No hay datos para mostrar",
  className = "",
  getRowClassName,
  exportConfig = null
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isExporting, setIsExporting] = useState(false);

  // Función para ordenar los datos
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Manejar valores nulos/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
      if (bValue == null) return sortConfig.direction === 'asc' ? 1 : -1;

      // Comparar fechas
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortConfig.direction === 'asc' 
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }

      // Comparar strings (case insensitive)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Comparar números
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Fallback: convertir a string y comparar
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      const comparison = aStr.localeCompare(bStr);
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sortConfig]);

  // Función para manejar el click en las columnas
  const handleSort = (columnKey) => {
    const column = columns.find(col => col.key === columnKey);
    if (!column || column.sortable === false) return;

    setSortConfig(prevConfig => ({
      key: columnKey,
      direction: prevConfig.key === columnKey && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const resolveExportRows = async () => {
    if (typeof exportConfig?.getRows === 'function') {
      return await exportConfig.getRows();
    }

    return sortedData;
  };

  const handleExportExcel = async () => {
    if (!exportConfig || sortedData.length === 0 || isExporting) return;
    setIsExporting(true);
    try {
      const rows = await resolveExportRows();
      exportTableAsExcel({
        rows,
        columns,
        fileName: exportConfig.fileName,
        title: exportConfig.title
      });
    } catch (error) {
      console.error('Error exportando tabla a Excel:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!exportConfig || sortedData.length === 0 || isExporting) return;
    setIsExporting(true);
    try {
      const rows = await resolveExportRows();
      await exportTableAsPdf({
        rows,
        columns,
        fileName: exportConfig.fileName,
        title: exportConfig.title
      });
    } catch (error) {
      console.error('Error exportando tabla a PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Función para obtener el ícono de ordenamiento
  const getSortIcon = (columnKey) => {
    const column = columns.find(col => col.key === columnKey);
    if (!column || column.sortable === false) return null;

    if (sortConfig.key !== columnKey) {
      return <i className="fas fa-sort sort-icon inactive"></i>;
    }

    return sortConfig.direction === 'asc' 
      ? <i className="fas fa-sort-up sort-icon active"></i>
      : <i className="fas fa-sort-down sort-icon active"></i>;
  };

  // Función para renderizar el contenido de una celda
  const renderCellContent = (item, column) => {
    const value = item[column.key];
    
    // Si hay una función de renderizado personalizada
    if (column.render && typeof column.render === 'function') {
      return column.render(value, item);
    }

    // Si hay un formateador
    if (column.formatter && typeof column.formatter === 'function') {
      return column.formatter(value);
    }

    // Renderizado por defecto
    if (value == null) return '-';
    
    // Si es una fecha, formatearla
    if (value instanceof Date) {
      return value.toLocaleDateString('es-AR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    return String(value);
  };

  if (loading) {
    return (
      <div className={`data-table-container ${className}`}>
        <div className="data-table-loading">
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <p>{loadingMessage}</p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={`data-table-container ${className}`}>
        <div className="data-table-empty">
          <div className="empty-icon">
            <i className="fas fa-inbox"></i>
          </div>
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`data-table-container ${className}`}>
      {exportConfig && (
        <div className="data-table-export-bar">
          <span className="data-table-export-label">Descargar tabla</span>
          <div className="data-table-export-actions">
            <button type="button" className="export-table-btn" onClick={handleExportExcel} disabled={isExporting}>
              <i className={`fas ${isExporting ? 'fa-spinner fa-spin' : 'fa-file-excel'}`}></i>
              Excel
            </button>
            <button type="button" className="export-table-btn" onClick={handleExportPdf} disabled={isExporting}>
              <i className={`fas ${isExporting ? 'fa-spinner fa-spin' : 'fa-file-pdf'}`}></i>
              PDF
            </button>
          </div>
        </div>
      )}
      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th 
                  key={column.key}
                  className={`
                    ${column.sortable !== false ? 'sortable' : ''} 
                    ${column.align ? `text-${column.align}` : ''} 
                    ${column.width ? `width-${column.width}` : ''}
                    ${column.className || ''}
                  `}
                  onClick={() => handleSort(column.key)}
                  style={column.style}
                >
                  <div className="th-content">
                    <span className="th-text">{column.title}</span>
                    {getSortIcon(column.key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, index) => (
              <tr
                key={item.id || index}
                className={`data-row ${typeof getRowClassName === 'function' ? getRowClassName(item, index) || '' : ''}`.trim()}
              >
                {columns.map((column) => (
                  <td 
                    key={`${item.id || index}-${column.key}`}
                    className={`
                      ${column.align ? `text-${column.align}` : ''} 
                      ${column.className || ''}
                    `}
                    style={column.style}
                  >
                    {renderCellContent(item, column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DataTable;
